'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { MotionLink } from '@/components/ui/Motion';
import { StaggeredText } from '@/components/ui/StaggeredText';
import { TypographyH2, TypographySmall } from '@/components/ui/typography';
import useGetImageUrlsByTags from '@/components/hooks/use-get-image-urls-by-tags';
import { URLS } from '@/lib/consts';
import { cls } from '@/lib/utils';
import BackgroundImage from '../../layout/background-image';
import SectionBase from '../section-base';
import {
  wrapperMotionProps,
  contentMotionProps,
  itemMotionProps,
  buttonMotionProps,
} from './animations';
import { REMODELS_TITLE, REMODELS_DESCRIPTION, REMODELS } from './consts';

const SECTION_ID = 'remodels';

function RemodelItem({ title, uri }: { title: string; uri: string }) {
  const tag = `hero${uri}`.replaceAll(/[\s/-]|(?:%20)/g, '_');
  const { data } = useGetImageUrlsByTags({ tags: tag });
  const img = data && data.length > 0 ? data[0].public_id : null;

  return (
    <motion.div {...itemMotionProps}>
      <Button
        className={cls`
          flex flex-col flex-1 gap-2
          h-auto w-full
          justify-center items-center
          p-0
          aspect-4/3
          text-center
        `}
        type="button"
        asChild
      >
        <MotionLink className={cls`relative`} href={uri}>
          <BackgroundImage
            src={img ?? ''}
            alt={title}
            fill
            sizes="(max-width: 640px) 100vw, 33vw"
            overlay="bg-black/30 hover:bg-black/50 transition-all duration-300"
            foreground="flex items-end justify-start p-4"
            className="absolute inset-0 rounded-lg overflow-hidden"
            imageProps={{ showSkeleton: true }}
          >
            <span
              className={cls`text-2xl md:text-3xl font-prata text-white`}
            >
              {title}
            </span>
          </BackgroundImage>
        </MotionLink>
      </Button>
    </motion.div>
  );
}

function RemodelsSection() {
  return (
    <SectionBase
      id={SECTION_ID}
      containerProps={{ id: SECTION_ID }}
      background="bg-card"
    >
      <motion.div
        className={cls`flex flex-col items-center gap-10`}
        {...wrapperMotionProps}
      >
        {/* Title */}
        <TypographyH2 className={cls`text-center border-b-0`}>
          <StaggeredText staggerChildren={0.03} delay={0.1}>
            {REMODELS_TITLE}
          </StaggeredText>
        </TypographyH2>

        {/* 3-column image tiles */}
        <motion.div
          className={cls`grid grid-cols-1 sm:grid-cols-3 gap-4 w-full`}
          {...contentMotionProps}
        >
          {REMODELS.map(({ title, uri }) => (
            <RemodelItem key={title} title={title} uri={uri} />
          ))}
        </motion.div>

        {/* Description + CTA */}
        <div
          className={cls`flex flex-col md:flex-row items-center gap-6 md:gap-10 w-full`}
        >
          <TypographySmall
            className={cls`text-center md:text-left text-muted-foreground flex-1`}
          >
            {REMODELS_DESCRIPTION}
          </TypographySmall>

          <Button asChild className="shrink-0">
            <motion.a href={URLS.REQUEST_CONSULTATION} {...buttonMotionProps}>
              Contact Us
            </motion.a>
          </Button>
        </div>
      </motion.div>
    </SectionBase>
  );
}

export default RemodelsSection;
